import React from 'react';
import { DatePicker, Input, InputNumber, Switch } from 'antd';
import dayjs from 'dayjs';
import type { ReportParameter } from '../../types';
import BranchSelect from './BranchSelect';
import GlProductSelect from './GlProductSelect';
import LoanProductSelect from './LoanProductSelect';
import DynamicSelect from './DynamicSelect';
import StaticSelect from './StaticSelect';
import LoanKeyModalSelect from './LoanKeyModalSelect';
import ProductSelect from './ProductSelect';
import RegionSelect from './RegionSelect';
import DistrictSelect from './DistrictSelect';
import WardSelect from './WardSelect';
import GroupSelect from './GroupSelect';
import LoanOfficerSelect from './LoanOfficerSelect';
import LoanSupervisorSelect from './LoanSupervisorSelect';
import BusinessSectorSelect from './BusinessSectorSelect';
import LoanFundSelect from './LoanFundSelect';

const { RangePicker } = DatePicker;

export interface ParameterInputFactoryProps {
  param: ReportParameter;
  loading?: boolean;
  form: any;
  reportCode?: string;
  reportCategory?: string;
  // Props injected by Form.Item
  value?: any;
  onChange?: (value: any) => void;
}

/**
 * Resolves the optionsEndpoint to a backend provider name for DynamicSelect.
 */
const resolveProviderName = (param: ReportParameter): string | undefined => {
  if (param.optionsEndpoint) {
    const endpoint = param.optionsEndpoint.trim();
    const match = endpoint.match(/select-field\/([^/]+)\/values/i);
    if (match?.[1]) return match[1];
    if (!endpoint.includes('/')) return endpoint;
  }
  return undefined;
};

/**
 * Factory component that maps a ReportParameter's type to the correct input component.
 *
 * This is the single place where parameter type → component mapping is defined.
 * To add a new parameter type:
 *   1. Create a new component in this directory
 *   2. Add a case to the switch below
 *   3. Add the type to ParameterType in types/index.ts
 */
const ParameterInputFactory: React.FC<ParameterInputFactoryProps> = ({
  param,
  loading = false,
  form,
  reportCode,
  reportCategory,
  value,
  onChange,
}) => {
  const disabled = loading;
  const placeholder = param.placeholder;

  switch (param.type) {
    // ======================== TEXT ========================
    case 'TEXT':
      if (param.name === 'branch') {
        return <BranchSelect disabled={disabled} placeholder={placeholder} />;
      }
      return (
        <Input
          placeholder={placeholder}
          disabled={disabled}
          maxLength={param.maxLength}
          minLength={param.minLength}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== NUMBER ========================
    case 'NUMBER':
      return (
        <InputNumber
          placeholder={placeholder}
          disabled={disabled}
          min={param.minValue}
          max={param.maxValue}
          className="w-full"
          value={value}
          onChange={onChange}
        />
      );

    // ======================== DATE ========================
    case 'DATE':
      return (
        <DatePicker
          id={param.name}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
          format="YYYY-MM-DD"
          value={value}
          onChange={onChange}
          disabledDate={(current) => {
            if (!param.allowFutureDates && current && current > dayjs().endOf('day')) {
              return true;
            }
            if (!param.allowPastDates && current && current < dayjs().startOf('day')) {
              return true;
            }
            return false;
          }}
        />
      );

    // ======================== DATE_RANGE ========================
    case 'DATE_RANGE':
      return (
        <RangePicker
          disabled={disabled}
          className="w-full"
          format="YYYY-MM-DD"
          value={value}
          onChange={onChange}
          placeholder={placeholder ? [placeholder, placeholder] : undefined}
        />
      );

    // ======================== BRANCH_SELECT ========================
    case 'BRANCH_SELECT':
      return <BranchSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== GL_SELECT ========================
    case 'GL_SELECT':
      return (
        <GlProductSelect
          param={param}
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== LOAN_PRODUCT_SELECT ========================
    case 'LOAN_PRODUCT_SELECT':
      return (
        <ProductSelect
          param={param}
          productType="L"
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== SAVINGS_PRODUCT_SELECT ========================
    case 'SAVINGS_PRODUCT_SELECT':
      return (
        <ProductSelect
          param={param}
          productType="S"
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== SHARES_PRODUCT_SELECT ========================
    case 'SHARES_PRODUCT_SELECT':
      return (
        <ProductSelect
          param={param}
          productType="H"
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== FIXED_DEPOSIT_PRODUCT_SELECT ========================
    case 'FIXED_DEPOSIT_PRODUCT_SELECT':
      return (
        <ProductSelect
          param={param}
          productType="F"
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== ACCOUNT_SELECT ========================
    case 'ACCOUNT_SELECT': {
      const provider = resolveProviderName(param) || 'accounts';
      return (
        <DynamicSelect
          param={param}
          disabled={disabled}
          placeholder={placeholder || 'Select account'}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          providerName={provider}
          value={value}
          onChange={onChange}
        />
      );
    }

    // ======================== CUSTOMER_SELECT ========================
    case 'CUSTOMER_SELECT': {
      const provider = resolveProviderName(param) || 'customers';
      return (
        <DynamicSelect
          param={param}
          disabled={disabled}
          placeholder={placeholder || 'Select customer'}
          form={form}
          reportCode={reportCode}
          reportCategory={reportCategory}
          providerName={provider}
          value={value}
          onChange={onChange}
        />
      );
    }

    // ======================== SELECT ========================
    case 'SELECT': {
      // Special case: loan_key for Loan Ledger Card
      if (reportCode === 'LOAN_LEDGER_CARD' && param.name === 'loan_key') {
        return (
          <LoanKeyModalSelect
            form={form}
            disabled={disabled}
            reportCode={reportCode}
            reportCategory={reportCategory}
            value={value}
            onChange={onChange}
          />
        );
      }

      // If the parameter has an optionsEndpoint, use DynamicSelect
      const provider = resolveProviderName(param);
      if (provider) {
        return (
          <DynamicSelect
            param={param}
            disabled={disabled}
            placeholder={placeholder}
            form={form}
            reportCode={reportCode}
            reportCategory={reportCategory}
            providerName={provider}
            value={value}
            onChange={onChange}
          />
        );
      }

      // Otherwise use StaticSelect with inline options
      return (
        <StaticSelect
          param={param}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      );
    }

    // ======================== MULTI_SELECT ========================
    case 'MULTI_SELECT':
      return (
        <StaticSelect
          param={param}
          disabled={disabled}
          placeholder={placeholder}
          mode="multiple"
          value={value}
          onChange={onChange}
        />
      );

    // ======================== REGION_SELECT ========================
    case 'REGION_SELECT':
      return <RegionSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== DISTRICT_SELECT ========================
    case 'DISTRICT_SELECT':
      return (
        <DistrictSelect
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          regionFieldName={param.dependsOn || 'region'}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== WARD_SELECT ========================
    case 'WARD_SELECT':
      return (
        <WardSelect
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          districtFieldName={param.dependsOn || 'district'}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== GROUP_SELECT ========================
    case 'GROUP_SELECT':
      return (
        <GroupSelect
          disabled={disabled}
          placeholder={placeholder}
          form={form}
          branchFieldName={param.dependsOn || 'branch'}
          value={value}
          onChange={onChange}
        />
      );

    // ======================== LOAN_OFFICER_SELECT ========================
    case 'LOAN_OFFICER_SELECT':
      return <LoanOfficerSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== LOAN_SUPERVISOR_SELECT ========================
    case 'LOAN_SUPERVISOR_SELECT':
      return <LoanSupervisorSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== BUSINESS_SECTOR_SELECT ========================
    case 'BUSINESS_SECTOR_SELECT':
      return <BusinessSectorSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== LOAN_FUND_SELECT ========================
    case 'LOAN_FUND_SELECT':
      return <LoanFundSelect disabled={disabled} placeholder={placeholder} value={value} onChange={onChange} />;

    // ======================== BOOLEAN ========================
    case 'BOOLEAN':
      return <Switch disabled={disabled} checked={value} onChange={onChange} />;

    // ======================== HIDDEN ========================
    case 'HIDDEN':
      return <Input type="hidden" value={value} onChange={onChange} />;

    // ======================== DEFAULT ========================
    default:
      return <Input placeholder={placeholder} disabled={disabled} value={value} onChange={onChange} />;
  }
};

export default ParameterInputFactory;
